import React, { useEffect, useState } from 'react';
import { useClient } from '../context/ClientContext';
import { useAuth } from '../context/AuthContext';
import {
  ChevronDown, Building2, LayoutDashboard, Users, ChefHat, Truck, RefreshCw,
  Folder, BookOpen, TrendingUp, DollarSign, MessageSquare, BarChart3, LogOut,
  FileText, Target, FileSpreadsheet, Menu as MenuIcon, ChevronLeft, ChevronRight,
  ShoppingBag, ShieldCheck, ScrollText, HardDrive, CalendarDays, AlertTriangle, Link2,
} from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Button } from './ui/button';

// Sections logically group the modules so the sidebar reads like a workflow.
//
// RBAC: items tagged `clientVisible: true` are the ONLY entries a role=client
// user is allowed to see. Everything else is hidden from the sidebar AND
// route-guarded by the RoleRoute wrapper in App.js.
const NAV_SECTIONS = [
  {
    label: 'Overview',
    items: [
      { path: '/dashboard',          label: 'Dashboard',      icon: LayoutDashboard, testid: 'nav-dashboard',          clientVisible: true },
      { path: '/financial-analysis', label: 'Financials',     icon: BarChart3,       testid: 'nav-financial-analysis', clientVisible: true },
    ],
  },
  {
    label: 'Operations',
    items: [
      { path: '/menu',         label: 'Menu',         icon: ChefHat,        testid: 'nav-menu',          clientVisible: true },
      { path: '/menu-engineering', label: 'Menu Engineering', icon: TrendingUp, testid: 'nav-menu-engineering' },
      { path: '/recipes',      label: 'Recipes',      icon: BookOpen,       testid: 'nav-recipes',       clientVisible: true },
      { path: '/suppliers',    label: 'Suppliers',    icon: Truck,          testid: 'nav-suppliers',     clientVisible: true },
      { path: '/expenses',     label: 'Expenses',     icon: DollarSign,     testid: 'nav-expenses',      clientVisible: true },
      { path: '/mandi-prices',  label: 'Mandi Prices',  icon: TrendingUp,     testid: 'nav-mandi' },
      { path: '/mandi-mapping', label: 'Mandi Mapping', icon: Target,         testid: 'nav-mandi-mapping' },
    ],
  },
  {
    label: 'Reconciliation',
    items: [
      { path: '/reconciliation', label: 'Reconciliation', icon: RefreshCw,       testid: 'nav-reconciliation', clientVisible: true },
      { path: '/daily-recon',    label: 'Daily Recon',    icon: CalendarDays,    testid: 'nav-daily-recon',    clientVisible: true },
      { path: '/reviews',        label: 'Reviews',        icon: MessageSquare,   testid: 'nav-reviews',        clientVisible: true },
      { path: '/benchmarks',     label: 'Benchmarks',     icon: Target,          testid: 'nav-benchmarks' },
    ],
  },
  {
    label: 'Output',
    items: [
      { path: '/reports',          label: 'Reports',          icon: FileText,         testid: 'nav-reports',   clientVisible: true },
      { path: '/movements',        label: 'Movements',        icon: TrendingUp,       testid: 'nav-movements', clientVisible: true },
      { path: '/tally',                  label: 'Tally',                  icon: FileSpreadsheet,  testid: 'nav-tally' },
      { path: '/pos-integrations',       label: 'POS Integrations',       icon: ShoppingBag,      testid: 'nav-pos-integrations' },
      { path: '/accounting-integrations', label: 'Accounting Integrations', icon: BookOpen,        testid: 'nav-accounting-integrations' },
      { path: '/files',            label: 'Files',            icon: Folder,           testid: 'nav-files' },
    ],
  },
  {
    label: 'Admin',
    items: [
      { path: '/clients',     label: 'Clients',     icon: Users,       testid: 'nav-clients' },
      { path: '/links',       label: 'Links',       icon: Link2,       testid: 'nav-links' },
      { path: '/exceptions',  label: 'Exceptions',  icon: AlertTriangle, testid: 'nav-exceptions' },
      { path: '/admin/files', label: 'File Storage', icon: HardDrive,  testid: 'nav-admin-files' },
      { path: '/admin/allowlist', label: 'Google Allowlist', icon: ShieldCheck, testid: 'nav-admin-allowlist' },
      { path: '/admin/costs', label: 'LLM Cost Meter', icon: BarChart3, testid: 'nav-admin-costs' },
      { path: '/security',    label: 'Security',    icon: ShieldCheck, testid: 'nav-security' },
      { path: '/audit-log',   label: 'Audit Log',   icon: ScrollText,  testid: 'nav-audit-log' },
    ],
  },
];

// Return only the sections/items the current role can see. Sections whose
// items are all hidden are dropped entirely.
const filterSectionsForRole = (role) => {
  if (role === 'client') {
    return NAV_SECTIONS
      .map((sec) => ({ ...sec, items: sec.items.filter((it) => it.clientVisible) }))
      .filter((sec) => sec.items.length > 0);
  }
  return NAV_SECTIONS;
};

export const Navigation = () => {
  const { clients, selectedClient, setSelectedClient } = useClient();
  const { user, logout } = useAuth();
  const location = useLocation();

  // Sidebar state: 'expanded' | 'collapsed' (icon-only) — persisted
  const [sidebarMode, setSidebarMode] = useState(() => {
    try {
      return localStorage.getItem('sidebarMode') || 'expanded';
    } catch (_) { return 'expanded'; }
  });
  // Mobile drawer state — overlay, separate from desktop collapse
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    try { localStorage.setItem('sidebarMode', sidebarMode); } catch (_) {}
  }, [sidebarMode]);

  const isActive = (path) => location.pathname === path;
  const collapsed = sidebarMode === 'collapsed';
  const visibleSections = filterSectionsForRole(user?.role);

  return (
    <>
      {/* === TOP BAR === */}
      <header
        className="sticky top-0 z-40 backdrop-blur-xl bg-white/85 border-b border-border shadow-sm"
        data-testid="main-topbar"
      >
        <div className="flex h-14 items-center justify-between px-4">
          <div className="flex items-center gap-3">
            {/* Mobile hamburger */}
            <button
              className="lg:hidden p-1.5 -ml-1 rounded hover:bg-background-secondary"
              onClick={() => setMobileOpen(true)}
              data-testid="mobile-nav-toggle"
              aria-label="Open navigation"
            >
              <MenuIcon className="h-5 w-5 text-text-primary" />
            </button>

            <Link to="/dashboard" className="flex items-center gap-2">
              <img
                src="https://static.prod-images.emergentagent.com/jobs/ea9cf972-3434-4165-bf74-a93c34e7a810/images/93589658ea558262a331193e658bbed9ba248c401b58657ed297db3b7130550f.png"
                alt="Epicurea Nexus"
                className="h-8 w-auto"
                data-testid="brand-logo"
              />
              <div className="hidden md:block leading-none">
                <div className="text-sm font-bold text-primary tracking-tight">Epicurea Nexus</div>
                <div className="text-[9px] text-text-muted tracking-[0.18em] uppercase mt-0.5">Profit Recovery</div>
              </div>
            </Link>
          </div>

          <div className="flex items-center gap-2">
            {selectedClient && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    className="border-border hover:border-primary transition-colors"
                    data-testid="client-selector-dropdown"
                  >
                    <Building2 className="mr-2 h-4 w-4" />
                    <span className="hidden sm:inline max-w-[160px] truncate">{selectedClient.name}</span>
                    <ChevronDown className="ml-2 h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64">
                  {clients.map((client) => (
                    <DropdownMenuItem
                      key={client.id}
                      onClick={() => setSelectedClient(client)}
                      className="cursor-pointer"
                      data-testid={`client-option-${client.id}`}
                    >
                      <div className="flex flex-col">
                        <span className="font-medium">{client.name}</span>
                        <span className="text-xs text-text-muted">{client.location}</span>
                      </div>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="flex items-center hover:bg-background-secondary rounded-md p-1 transition-colors"
                  data-testid="user-menu"
                  aria-label="User menu"
                >
                  <img
                    src="https://static.prod-images.emergentagent.com/jobs/ea9cf972-3434-4165-bf74-a93c34e7a810/images/a228244fc9f3ae93e8260b395fde07b096052f6ce1ae8bc0e0a5730e56bb335e.png"
                    alt="User"
                    className="h-8 w-8 rounded-full border-2 border-border"
                    data-testid="user-avatar"
                  />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium">{user?.name || 'Admin'}</p>
                  <p className="text-xs text-text-muted">{user?.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={logout}
                  className="cursor-pointer text-state-error"
                  data-testid="logout-button"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* === DESKTOP SIDEBAR (lg+) === */}
      <aside
        className={`hidden lg:flex flex-col fixed top-14 left-0 bottom-0 z-30 bg-white border-r border-border transition-[width] duration-200 ease-in-out
          ${collapsed ? 'w-16' : 'w-60'}`}
        data-testid="sidebar"
        data-state={sidebarMode}
      >
        {/* Collapse toggle pinned at the TOP of the sidebar so it can never be covered by the AI FAB */}
        <div className={`flex ${collapsed ? 'justify-center' : 'justify-end'} px-2 pt-2`}>
          <button
            onClick={() => setSidebarMode(collapsed ? 'expanded' : 'collapsed')}
            className="p-1.5 rounded-md hover:bg-background-secondary text-text-secondary border border-transparent hover:border-border"
            data-testid="sidebar-collapse-toggle"
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          </button>
        </div>
        <nav className="flex-1 overflow-y-auto py-3 px-2" data-testid="sidebar-nav">
          {visibleSections.map((sec, idx) => (
            <SidebarSection
              key={sec.label}
              section={sec}
              isFirst={idx === 0}
              collapsed={collapsed}
              isActive={isActive}
            />
          ))}
        </nav>
      </aside>

      {/* === MOBILE DRAWER (below lg) === */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 z-50 bg-black/50"
          onClick={() => setMobileOpen(false)}
          data-testid="mobile-nav-overlay"
        >
          <aside
            className="absolute top-0 left-0 bottom-0 w-72 bg-white shadow-xl flex flex-col"
            onClick={(e) => e.stopPropagation()}
            data-testid="mobile-sidebar"
          >
            <div className="flex items-center justify-between px-4 h-14 border-b border-border">
              <span className="text-sm font-bold text-primary">Menu</span>
              <button
                onClick={() => setMobileOpen(false)}
                className="p-1.5 rounded hover:bg-background-secondary"
                aria-label="Close navigation"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
            </div>
            <nav className="flex-1 overflow-y-auto py-3 px-2">
              {visibleSections.map((sec, idx) => (
                <SidebarSection
                  key={sec.label}
                  section={sec}
                  isFirst={idx === 0}
                  collapsed={false}
                  isActive={isActive}
                  onItemClick={() => setMobileOpen(false)}
                />
              ))}
            </nav>
          </aside>
        </div>
      )}
    </>
  );
};

// Sidebar section block — section label + nav items
const SidebarSection = ({ section, isFirst, collapsed, isActive, onItemClick }) => (
  <div className={isFirst ? '' : 'mt-4'}>
    {!collapsed && (
      <div className="px-3 mb-1 text-[10px] uppercase tracking-[0.18em] font-semibold text-text-muted">
        {section.label}
      </div>
    )}
    {collapsed && !isFirst && (
      <div className="h-px bg-border mx-2 mb-2" aria-hidden />
    )}
    <ul className="space-y-0.5">
      {section.items.map(item => {
        const Icon = item.icon;
        const active = isActive(item.path);
        return (
          <li key={item.path}>
            <Link
              to={item.path}
              onClick={onItemClick}
              className={`group flex items-center ${collapsed ? 'justify-center' : 'gap-3'} px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                active
                  ? 'bg-primary text-white shadow-sm'
                  : 'text-text-secondary hover:bg-background-secondary hover:text-text-primary'
              }`}
              data-testid={item.testid}
              title={collapsed ? item.label : undefined}
            >
              <Icon className={`h-4 w-4 flex-shrink-0 ${active ? '' : 'text-text-muted group-hover:text-text-primary'}`} />
              {!collapsed && <span className="truncate">{item.label}</span>}
            </Link>
          </li>
        );
      })}
    </ul>
  </div>
);
